/**
 * CategoryDifferencesNote Component
 *
 * A small warning note shown beneath a routine surveillance scatter plot when
 * the plotted rows are not uniform across the "category" columns (see
 * CATEGORY_COLUMNS). It renders a warning icon + "Category differences" text,
 * with a tooltip listing exactly which category columns differ.
 *
 * Renders nothing when there are no differences, so callers can drop it under
 * every plot unconditionally.
 */

import { TriangleAlert } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

/** Props for the CategoryDifferencesNote component. */
interface CategoryDifferencesNoteProps {
  /**
   * The display labels of the category columns that differ across the plot's
   * rows (from computeCategoryDifferences). When empty, the note renders
   * nothing.
   */
  differingLabels: string[]
}

/**
 * Warning note flagging category-column differences within a single plot.
 *
 * @param props - Component props.
 * @param props.differingLabels - Labels of the differing category columns.
 *        When empty, nothing is rendered.
 * @returns The note (icon + text + tooltip), or null when there are no
 *          differences to report.
 */
export default function CategoryDifferencesNote({
  differingLabels,
}: CategoryDifferencesNoteProps): React.JSX.Element | null {
  // No differences → no note. Lets callers render it unconditionally.
  if (differingLabels.length === 0) {
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A button so the tooltip is keyboard-focusable and accessible, not
            just hover-only. Styled as inline text, not a button. */}
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500 cursor-help"
          aria-label="Diverse categories — details"
        >
          <TriangleAlert className="size-3.5 shrink-0" aria-hidden="true" />
          Diverse categories
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p className="font-medium">Differing category columns:</p>
        <ul className="mt-1 list-disc pl-4">
          {differingLabels.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  )
}
